import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, MapPin, Users, Heart, MessageSquare, 
  Crown, Star, Filter, Search, X, Mail, Calendar,
  Globe, ChevronRight, Eye, Sparkles, User, Zap, TrendingUp, Lock, EyeOff, Send, Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AnimatedAvatar from "../components/AnimatedAvatar";
import MembershipGuardModal, { canViewMembers, canSendDM } from "../components/MembershipGuardModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 멤버십 레벨 순서 (숫자가 높을수록 높은 등급)
const MEMBERSHIP_LEVELS = {
  "basic": 0,      // 일반
  "standard": 1,   // 정회원 (과거 변환 잔여 별칭 → regular와 동일 취급)
  "regular": 1,    // 정회원
  "premium": 2,    // 프리미엄
  "vip": 3         // VIP
};

// 사용자가 상대방의 사진을 볼 수 있는지 체크
const canViewPhoto = (viewerMembership, targetMembership, isAuthenticated) => {
  const targetLevel = MEMBERSHIP_LEVELS[targetMembership] ?? 0;
  
  // 일반회원(basic)의 사진은 누구나 볼 수 있음
  if (targetLevel === 0) {
    return true;
  }
  
  // 비로그인은 일반회원 외 사진 못 봄
  if (!isAuthenticated) {
    return false;
  }
  
  const viewerLevel = MEMBERSHIP_LEVELS[viewerMembership] ?? 0;
  
  // 내 등급 이하의 회원 사진만 볼 수 있음
  return viewerLevel >= targetLevel;
};

// 이름 마스킹: 내 등급보다 높은 회원의 이름은 "김OO" 형태로 표시
const getDisplayName = (viewerMembership, targetMembership, name) => {
  const viewerLevel = MEMBERSHIP_LEVELS[viewerMembership] ?? 0;
  const targetLevel = MEMBERSHIP_LEVELS[targetMembership] ?? 0;

  // VIP는 모든 회원 실명 볼 수 있음
  // 같은 등급이거나 내 등급 이상이면 실명 표시
  if (viewerLevel >= targetLevel) return name;

  // 상대 등급이 더 높으면 마스킹
  const firstChar = name?.charAt(0) || "";
  return `${firstChar}OO`;
};

// 지역 카테고리 (기본 - API에서 동적으로 업데이트됨)
const FLAG_MAP = {
  KR: "🇰🇷", US: "🇺🇸", CA: "🇨🇦", AU: "🇦🇺", JP: "🇯🇵", CN: "🇨🇳",
  GB: "🇬🇧", DE: "🇩🇪", FR: "🇫🇷", SG: "🇸🇬", NZ: "🇳🇿", TH: "🇹🇭",
  VN: "🇻🇳", PH: "🇵🇭", ID: "🇮🇩", MY: "🇲🇾", CH: "🇨🇭", MX: "🇲🇽",
};

const REGION_ID_MAP = {
  KR: "korea", US: "usa", CA: "canada", AU: "australia", JP: "japan", CN: "china",
  GB: "uk", DE: "germany", FR: "france", SG: "singapore", NZ: "newzealand",
  TH: "thailand", VN: "vietnam", PH: "philippines", ID: "indonesia", MY: "malaysia",
  CH: "switzerland", MX: "mexico",
};

// 가짜/샘플 회원 데이터는 사용하지 않습니다 (실제 DB 회원만 표시)
const sampleMembers = [];

export default function MembersPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, token, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const regionParam = searchParams.get("region") || "all";
  const [accessGuard, setAccessGuard] = useState(null); // null | "auth" | "upgrade"
  const [showGuardModal, setShowGuardModal] = useState(false);

  // Deep-link 방어: 정회원이 아니면 가드 모달 표시
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setAccessGuard("auth");
      setShowGuardModal(true);
    } else if (!canViewMembers(user)) {
      setAccessGuard("upgrade");
      setShowGuardModal(true);
    } else {
      setAccessGuard(null);
      setShowGuardModal(false);
    }
  }, [authLoading, isAuthenticated, user]);
  
  const [selectedRegion, setSelectedRegion] = useState(regionParam);
  const [genderFilter, setGenderFilter] = useState("all");
  const [verifiedFirst, setVerifiedFirst] = useState(false);
  const [sortBy, setSortBy] = useState("latest"); // latest | age | membership
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendingInterest, setSendingInterest] = useState(false);
  const [interestSent, setInterestSent] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [regions, setRegions] = useState([{ id: "all", label: "전체", code: "ALL" }]);
  const [realMembers, setRealMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 24;
  
  // Compute region from location string (used to map DB members into UI categories)
  const detectRegion = (location = "") => {
    const loc = (location || "").toLowerCase();
    if (/(서울|부산|대구|인천|광주|대전|울산|세종|경기|강원|충북|충남|전북|전남|경북|경남|제주|한국|korea|seoul|busan|incheon|daegu)/i.test(loc)) return "korea";
    if (/(usa|미국|los\s*angeles|new\s*york|california|texas|chicago|atlanta|seattle)/i.test(loc)) return "usa";
    if (/(canada|캐나다|toronto|vancouver|montreal|calgary|ottawa)/i.test(loc)) return "canada";
    if (/(australia|호주|sydney|melbourne|brisbane|perth)/i.test(loc)) return "australia";
    if (/(uk|영국|london|england)/i.test(loc)) return "uk";
    if (/(japan|일본|tokyo|osaka|kyoto)/i.test(loc)) return "japan";
    if (/(china|중국|beijing|shanghai)/i.test(loc)) return "china";
    if (/(singapore|싱가포르)/i.test(loc)) return "singapore";
    if (/(germany|독일|berlin|munich)/i.test(loc)) return "germany";
    if (/(france|프랑스|paris)/i.test(loc)) return "france";
    return "other";
  };

  const mapApiMember = (m) => {
    const age = m.birth_year && m.birth_year > 1900 ? new Date().getFullYear() - m.birth_year : null;
    return {
      id: `db_${m.id}`,
      _dbId: m.id,
      _source: "db",
      region: detectRegion(m.location),
      // 회원보기/프로필에서는 실명(정회원 이상) 우선 노출. 닉네임은 커뮤니티에서만 사용.
      username: m.display_name || m.username || "회원",
      nickname: m.username || "회원",
      age: age || 0,
      gender: m.gender || "",
      location: m.location || "",
      job: m.job || "",
      personality: m.personality || [],
      introduction: m.introduction || "프로필을 작성 중인 회원입니다.",
      profileImage: m.profile_image
        ? (m.profile_image.startsWith("http") ? m.profile_image : `${API_URL}${m.profile_image}`)
        : null,
      // 'standard'는 과거 변환 잔여 별칭 → 정회원(regular)으로 정규화 (관리자 표기와 일치)
      membershipType: ((m.subscription_tier || "basic").toLowerCase() === "standard")
        ? "regular"
        : (m.subscription_tier || "basic"),
      interests: m.interests || [],
      isOnline: false,
      lastActive: m.created_at ? new Date(m.created_at).toLocaleDateString("ko-KR") : "",
      _createdAt: m.created_at || "",
      document_verified: !!m.document_verified,
      hidden: !!m.hidden_from_listing,
    };
  };

  // 최근 가입자(API) 불러오기 → 항상 최상단에 노출
  // 페이지네이션을 위해 충분히 큰 limit(500)로 한번에 fetch (필터·검색은 클라이언트에서)
  useEffect(() => {
    // 인증 복원이 끝난 뒤에만 fetch — 그렇지 않으면 token=null 요청(일반)과
    // token 로드 후 요청(관리자/숨김포함)이 경쟁(race)하여 가벼운 응답이 덮어쓰는 버그 발생.
    if (authLoading) return;
    let ignore = false;
    const fetchRealMembers = async () => {
      try {
        // 토큰이 있으면 include_hidden 요청 (백엔드가 '관리자'에게만 실제로 숨김 회원 포함)
        const url = token
          ? `${API_URL}/api/members/public?limit=2000&include_hidden=true`
          : `${API_URL}/api/members/public?limit=2000`;
        const res = await fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
        if (!res.ok) return;
        const data = await res.json();
        if (ignore) return;
        const mapped = (data.members || []).map(mapApiMember);
        setRealMembers(mapped);
      } catch (e) {
        console.error("Failed to fetch real members:", e);
      }
    };
    fetchRealMembers();
    return () => { ignore = true; };
  }, [API_URL, token, authLoading]);

  // 관리자: 회원 카드에서 직접 노출/숨김 토글
  const handleToggleHide = async (member) => {
    if (!member?._dbId) return;
    try {
      const targetHidden = !member.hidden;
      const res = await fetch(`${API_URL}/api/admin/users/${member._dbId}/toggle-hide`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ hidden: targetHidden }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "변경 실패");
      const nowHidden = data.hidden_from_listing ?? targetHidden;
      setRealMembers((prev) =>
        prev.map((m) => (m._dbId === member._dbId ? { ...m, hidden: nowHidden } : m))
      );
      toast.success(nowHidden ? "이 회원을 목록에서 숨겼어요." : "이 회원을 다시 노출했어요.");
    } catch (e) {
      toast.error(e.message || "변경에 실패했어요.");
    }
  };

  // 실제 가입자 + 샘플 회원 병합 (DB 회원이 우선/최신순으로 위)
  const allMembers = useMemo(() => {
    const dbIds = new Set(realMembers.map(m => m._dbId));
    return [...realMembers, ...sampleMembers.filter(s => !dbIds.has(s.id))];
  }, [realMembers]);

  // 실제 회원이 존재하는 국가(region)만 칩으로 노출 — 회원 0명인 국가는 숨김
  const availableRegionIds = useMemo(
    () => new Set(allMembers.map(m => m.region)),
    [allMembers]
  );
  const visibleRegions = useMemo(
    () => regions.filter(r => r.code === "ALL" || availableRegionIds.has(r.id)),
    [regions, availableRegionIds]
  );
  
  // AI Recommendations state
  // 현재 사용자의 멤버십 등급 (로그인 안 했으면 basic)
  const myMembership = user?.membership_type || user?.membershipType || "basic";

  // 특정 회원의 사진을 볼 수 있는지 체크
  const canIViewPhoto = (targetMembership) => {
    return canViewPhoto(myMembership, targetMembership, isAuthenticated);
  };

  // 특정 회원의 이름 표시 (마스킹 적용)
  const getNameForDisplay = (member) => {
    return getDisplayName(myMembership, member.membershipType, member.username);
  };

  useEffect(() => {
    setSelectedRegion(regionParam);
  }, [regionParam]);

  // Fetch dynamic country list from API
  useEffect(() => {
    // 표시 제외할 국가 (회원 수 너무 적거나 비활성 국가)
    const EXCLUDED_COUNTRIES = ["Algeria", "Costa Rica", "Korea North", "North Korea"];
    const fetchCountries = async () => {
      try {
        const res = await fetch(`${API_URL}/api/members/countries`);
        if (res.ok) {
          const data = await res.json();
          const dynamicRegions = [{ id: "all", label: "전체", code: "ALL" }];
          (data.countries || []).forEach(c => {
            if (EXCLUDED_COUNTRIES.includes(c.label) || EXCLUDED_COUNTRIES.includes(c.code)) return;
            dynamicRegions.push({
              id: REGION_ID_MAP[c.code] || c.code.toLowerCase(),
              label: c.label,
              code: c.code,
              flag: FLAG_MAP[c.code] || "🌐",
            });
          });
          setRegions(dynamicRegions);
        }
      } catch (e) { console.error("Failed to fetch countries:", e); }
    };
    fetchCountries();
  }, [API_URL]);

  const handleRegionChange = (regionId) => {
    setSelectedRegion(regionId);
    if (regionId === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ region: regionId });
    }
  };

  // 로그인 사용자의 성별 정규화 (한글/영문 모두 처리)
  const normalizeGender = (g) => {
    if (!g) return null;
    const v = String(g).toLowerCase().trim();
    if (["male", "m", "남성", "남자"].includes(v)) return "male";
    if (["female", "f", "여성", "여자"].includes(v)) return "female";
    return null;
  };
  const myGender = normalizeGender(user?.gender);
  const isAdmin = user?.role === "admin";
  // 관리자는 자동 이성 필터 적용 안 함 (남녀 모두 선택적으로 볼 수 있음)
  const applyAutoOppositeFilter = myGender && !isAdmin;
  const oppositeGender = myGender === "male" ? "female" : (myGender === "female" ? "male" : null);

  const filteredMembers = allMembers.filter(member => {
    const mGender = normalizeGender(member.gender);
    // 일반 회원 로그인 시: 이성 프로필만 표시 (관리자는 제외)
    if (applyAutoOppositeFilter && mGender && mGender === myGender) {
      return false;
    }

    // Region filter
    if (selectedRegion !== "all" && member.region !== selectedRegion) {
      return false;
    }
    
    // Gender filter (수동 - 비로그인/관리자 사용 가능, 일반 회원은 자동 이성 필터로 대체됨)
    if (!applyAutoOppositeFilter && genderFilter !== "all" && mGender !== genderFilter) {
      return false;
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!member.username.toLowerCase().includes(query) &&
          !member.location.toLowerCase().includes(query) &&
          !member.introduction.toLowerCase().includes(query)) {
        return false;
      }
    }
    
    return true;
  });

  // Sort: 정렬 옵션 (최신순/나이순/회원등급순) + 인증회원 우선 필터
  const sortedMembers = useMemo(() => {
    let arr = [...filteredMembers];
    // "인증회원 우선 (정회원 이상)" 토글: basic(일반회원) 제외
    if (verifiedFirst) {
      arr = arr.filter((m) => (MEMBERSHIP_LEVELS[m.membershipType] ?? 0) >= 1);
    }
    if (sortBy === "age") {
      // 나이순 (어린 순). 나이 없는 회원(0)은 뒤로.
      arr.sort((a, b) => {
        const aAge = a.age || 9999;
        const bAge = b.age || 9999;
        return aAge - bAge;
      });
    } else if (sortBy === "membership") {
      // 회원등급순: VIP → 프리미엄 → 정회원 → 일반
      arr.sort((a, b) => {
        const aLv = MEMBERSHIP_LEVELS[a.membershipType] ?? 0;
        const bLv = MEMBERSHIP_LEVELS[b.membershipType] ?? 0;
        return bLv - aLv;
      });
    } else {
      // 최신순 (created_at desc)
      arr.sort((a, b) => {
        const aT = a._createdAt || "";
        const bT = b._createdAt || "";
        return bT.localeCompare(aT);
      });
    }
    // 인증회원 우선 토글이 ON이면, 같은 등급 내에서 문서 인증된 회원을 먼저
    if (verifiedFirst) {
      arr.sort((a, b) => (b.document_verified ? 1 : 0) - (a.document_verified ? 1 : 0));
    }
    return arr;
  }, [filteredMembers, sortBy, verifiedFirst]);

  // 페이지네이션 — 필터 변경 시 첫 페이지로 리셋
  const totalCount = sortedMembers.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pagedMembers = sortedMembers.slice((safeCurrentPage - 1) * PAGE_SIZE, safeCurrentPage * PAGE_SIZE);

  // 필터/검색 변경 시 1페이지로 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedRegion, genderFilter, verifiedFirst, sortBy, searchQuery]);

  const getMembershipBadge = (type) => {
    switch(type) {
      case "vip":
        return { label: "VIP", color: "bg-gold-500 text-stone-900", icon: Crown };
      case "premium":
        return { label: "프리미엄", color: "bg-rose-500 text-white", icon: Star };
      case "regular":
      case "standard":
        return { label: "정회원", color: "bg-blue-500 text-white", icon: User };
      default:
        return { label: "일반", color: "bg-stone-400 text-white", icon: User };
    }
  };

  const [memberFullProfile, setMemberFullProfile] = useState(null);

  const openProfileModal = async (member) => {
    const myLevel = MEMBERSHIP_LEVELS[myMembership] ?? 0;
    const targetLevel = MEMBERSHIP_LEVELS[member.membershipType] ?? 0;
    if (myLevel < 1 && targetLevel >= 1) {
      setShowUpgradeModal(true);
      return;
    }
    setSelectedMember(member);
    setMemberFullProfile(null);
    setInterestSent(false);
    setMessageSent(false);
    setShowProfileModal(true);

    // 정회원 이상이면 상세 프로필 가져오기
    if (myLevel >= 1 && token) {
      try {
        const profileId = member._dbId || member.id;
        const res = await fetch(`${API_URL}/api/members/${profileId}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setMemberFullProfile(await res.json());
      } catch { /* ignore profile fetch error */ }
    }
  };

  const handleSendInterest = async () => {
    if (!selectedMember) return;
    if (!isAuthenticated || !token) {
      alert("로그인이 필요합니다.");
      return;
    }
    // 실제 DB user_id는 _dbId 에 저장되어 있음 ('db_xxx' 가 아님)
    const targetId = selectedMember._dbId || selectedMember.id;
    if (!targetId || String(targetId).startsWith("db_")) {
      alert("회원 정보가 올바르지 않습니다. 새로고침 후 다시 시도해주세요.");
      return;
    }
    setSendingInterest(true);
    try {
      const res = await fetch(`${API_URL}/api/interests`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target_user_id: String(targetId) }),
      });
      if (res.ok) {
        setInterestSent(true);
      } else if (res.status === 400) {
        setInterestSent(true);
      } else {
        try {
          const data = await res.clone().json();
          alert(data.detail || "관심 보내기에 실패했습니다.");
        } catch {
          alert("관심 보내기에 실패했습니다.");
        }
      }
    } catch {
      alert("관심 보내기에 실패했습니다.");
    }
    setSendingInterest(false);
  };

  const handleSendMessage = async () => {
    if (!selectedMember || !token || !messageContent.trim()) return;
    const recvId = selectedMember._dbId || selectedMember.id;
    if (!recvId || String(recvId).startsWith("db_")) {
      alert("회원 정보가 올바르지 않습니다. 새로고침 후 다시 시도해주세요.");
      return;
    }
    setSendingMessage(true);
    try {
      const res = await fetch(`${API_URL}/api/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ receiver_id: String(recvId), content: messageContent.trim() }),
      });
      if (res.ok) {
        setMessageSent(true);
        setShowMessageModal(false);
        setMessageContent("");
      } else {
        try {
          const data = await res.clone().json();
          alert(data.detail || "메시지 보내기에 실패했습니다.");
        } catch {
          alert("메시지 보내기에 실패했습니다.");
        }
      }
    } catch {
      alert("메시지 보내기에 실패했습니다.");
    }
    setSendingMessage(false);
  };

  return (
    <div className="min-h-screen bg-stone-100" data-testid="members-page">
      {/* Access guard — 정회원만 통과 */}
      {accessGuard && (
        <div className="pt-28 pb-24 min-h-screen flex items-center justify-center px-4" data-testid="members-access-blocked">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-rose-100 to-amber-100 mb-5">
              <Lock className="w-9 h-9 text-burgundy-600" strokeWidth={1.5} />
            </div>
            <h1 className="font-serif text-2xl text-stone-900 mb-2">
              {accessGuard === "auth" ? "로그인이 필요한 페이지예요" : "정회원 전용 공간이에요"}
            </h1>
            <p className="text-stone-500 text-[15px] leading-relaxed mb-6">
              회원목록은 따뜻한 만남을 시작한 분들께만 열려있어요.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="text-burgundy-600 hover:text-burgundy-700 text-sm font-semibold underline underline-offset-4"
            >
              ← 뒤로 가기
            </button>
          </div>
          <MembershipGuardModal
            open={showGuardModal}
            reason={accessGuard}
            onClose={() => setShowGuardModal(false)}
          />
        </div>
      )}

      {!accessGuard && (<>
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-stone-100">
        <div className="max-w-[95%] mx-auto">
          <div className="relative rounded-3xl overflow-hidden shadow-lg min-h-[300px] md:min-h-[350px]">
            {/* Background */}
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1600')`
              }}
            />
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-burgundy-900/70 via-burgundy-800/50 to-transparent" />
            
            {/* Content */}
            <div className="relative z-10 p-8 md:p-12 flex flex-col justify-center h-full min-h-[300px] md:min-h-[350px]">
              <button
                onClick={() => navigate("/")}
                className="flex items-center gap-2 text-burgundy-200 hover:text-white mb-6 transition-colors w-fit"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>홈으로 돌아가기</span>
              </button>

              <div className="max-w-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-burgundy-200 text-sm font-medium tracking-wider uppercase">
                    New Coupling Members
                  </span>
                </div>

                <h1 className="font-sans text-4xl md:text-5xl font-[720] text-white mb-4">
                  회원정보보기
                </h1>
                
                <p className="text-white/90 text-lg leading-relaxed">
                  전 세계에서 활동하는 New Coupling 회원들을 만나보세요.<br />
                  진솔한 프로필로 당신의 인연을 찾아드립니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Region Tabs */}
      <section className="py-6 bg-stone-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {visibleRegions.map((region) => {
              const isActive = selectedRegion === region.id;
              return (
                <button
                  key={region.id}
                  onClick={() => handleRegionChange(region.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                    isActive
                      ? "bg-burgundy-600 text-white shadow-lg"
                      : "bg-white text-stone-600 hover:bg-burgundy-50 hover:text-burgundy-600 border border-stone-200"
                  }`}
                  data-testid={`region-${region.id}`}
                >
                  {region.code === "ALL" ? (
                    <Globe className="w-5 h-5" />
                  ) : (
                    <img 
                      src={`https://flagcdn.com/w40/${region.code.toLowerCase()}.png`}
                      alt={region.label}
                      className="w-6 h-4 object-cover rounded-sm"
                    />
                  )}
                  {region.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-6 bg-stone-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-stone-200">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  type="text"
                  placeholder="이름, 지역, 소개로 검색..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                />
              </div>
              
              {/* Gender Filter — 일반 회원 로그인 시 이성 자동 표시 (잠금) / 관리자는 자유 선택 */}
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-stone-400" />
                {applyAutoOppositeFilter ? (
                  <div className="px-4 py-3 border border-pink-200 bg-pink-50 rounded-xl flex items-center gap-2" data-testid="gender-auto-locked">
                    <span className="text-sm text-stone-600">이성 회원만 표시:</span>
                    <span className="text-sm font-bold text-burgundy-700">
                      {oppositeGender === "male" ? "👔 남성" : "👗 여성"}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <select
                      value={genderFilter}
                      onChange={(e) => setGenderFilter(e.target.value)}
                      className="px-4 py-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-burgundy-400 focus:border-transparent"
                      data-testid="gender-filter-select"
                    >
                      <option value="all">전체 성별</option>
                      <option value="male">남성</option>
                      <option value="female">여성</option>
                    </select>
                    {isAdmin && (
                      <span className="text-[11px] px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-md font-medium">
                        🛡 관리자 모드
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Sort Dropdown - 최신순/나이순/회원등급순 */}
              <div className="relative">
                <select
                  data-testid="members-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none pl-9 pr-8 py-2.5 rounded-xl border border-stone-200 bg-white text-sm font-medium text-stone-700 hover:border-burgundy-300 focus:outline-none focus:ring-2 focus:ring-burgundy-400 cursor-pointer"
                >
                  <option value="latest">최신순</option>
                  <option value="age">나이순</option>
                  <option value="membership">회원등급순</option>
                </select>
                <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>

              {/* Verified First Toggle */}
              <button
                data-testid="verified-first-toggle"
                onClick={() => setVerifiedFirst(!verifiedFirst)}
                className={`flex flex-col items-center justify-center gap-0.5 px-4 py-2 rounded-xl border transition-all font-medium text-sm leading-tight ${
                  verifiedFirst
                    ? "bg-cyan-50 border-cyan-400 text-cyan-700"
                    : "border-stone-200 text-stone-500 hover:border-cyan-300"
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>
                  인증회원 우선
                </span>
                <span className="text-[10px] font-normal opacity-80">(정회원 이상)</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Members Grid */}
      <section className="pb-16 bg-stone-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <p className="text-stone-600">
              총 <span className="font-bold text-burgundy-600" data-testid="members-total-count">{totalCount.toLocaleString()}</span>명의 회원
              {totalPages > 1 && (
                <span className="text-stone-400 text-sm ml-2">
                  · {safeCurrentPage} / {totalPages} 페이지 (페이지당 {PAGE_SIZE}명)
                </span>
              )}
            </p>
          </div>

          {totalCount === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl">
              <Users className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">검색 결과가 없습니다.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {pagedMembers.map((member) => {
                const badge = getMembershipBadge(member.membershipType);
                const BadgeIcon = badge.icon;
                const canView = canIViewPhoto(member.membershipType);
                
                return (
                  <motion.div
                    key={member.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-200 hover:shadow-lg transition-all group cursor-pointer"
                    onClick={() => openProfileModal(member)}
                  >
                    {/* Profile Image */}
                    <div className="relative h-64 overflow-hidden">
                      {isAdmin && member._source === "db" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleHide(member); }}
                          className={`absolute top-2 left-2 z-20 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold shadow-md transition-colors ${
                            member.hidden
                              ? "bg-stone-700/90 text-white hover:bg-stone-800"
                              : "bg-white/95 text-burgundy-700 hover:bg-white"
                          }`}
                          data-testid={`member-toggle-hide-${member._dbId}`}
                          title={member.hidden ? "현재 숨김 — 클릭하면 노출" : "현재 노출 — 클릭하면 숨김"}
                        >
                          {member.hidden ? <><EyeOff className="w-3.5 h-3.5" /> 숨김</> : <><Eye className="w-3.5 h-3.5" /> 노출중</>}
                        </button>
                      )}
                      {canView ? (
                        <>
                          {member.profileImage ? (
                            <img
                              src={member.profileImage}
                              alt={member.username}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const fb = e.currentTarget.parentElement.querySelector('.avatar-fallback');
                                if (fb) fb.style.display = "block";
                              }}
                            />
                          ) : (
                            <AnimatedAvatar name={member.username} gender={member.gender} />
                          )}
                          {/* 이미지 로드 실패 시 이니셜 아바타로 자동 대체 (기본 숨김) */}
                          <div className="avatar-fallback absolute inset-0" style={{ display: "none" }}>
                            <AnimatedAvatar name={member.username} gender={member.gender} />
                          </div>
                        </>
                      ) : (
                        <>
                          {member.profileImage ? (
                            <img
                              src={member.profileImage}
                              alt={member.username}
                              className="w-full h-full object-cover blur-xl scale-110"
                            />
                          ) : (
                            <div className="w-full h-full blur-sm">
                              <AnimatedAvatar name={member.username} gender={member.gender} />
                            </div>
                          )}
                          {/* Lock Overlay */}
                          <div className="absolute inset-0 bg-stone-900/40 flex flex-col items-center justify-center">
                            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3">
                              <Lock className="w-8 h-8 text-white" />
                            </div>
                            <p className="text-white text-sm font-medium text-center px-4">
                              {!isAuthenticated ? "로그인 필요" : "멤버십 업그레이드 필요"}
                            </p>
                            <p className="text-white/70 text-xs mt-1">
                              {badge.label} 이상만 열람 가능
                            </p>
                          </div>
                        </>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                      
                      {/* Online Status */}
                      <div className="absolute top-3 left-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${
                          member.isOnline 
                            ? "bg-green-500 text-white" 
                            : "bg-stone-500/80 text-white"
                        }`}>
                          <span className={`w-2 h-2 rounded-full ${member.isOnline ? "bg-white animate-pulse" : "bg-stone-300"}`} />
                          {member.isOnline ? "온라인" : member.lastActive}
                        </span>
                      </div>
                      
                      {/* Membership Badge */}
                      <div className="absolute top-3 right-3 flex items-center gap-1">
                        {member.document_verified && (
                          <span className="px-1.5 py-1 text-xs font-bold rounded-full flex items-center gap-0.5 bg-cyan-500 text-white" title="서류인증">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>
                          </span>
                        )}
                        <span className={`px-2 py-1 text-xs font-bold rounded-full flex items-center gap-1 ${badge.color}`}>
                          <BadgeIcon className="w-3 h-3" />
                          {badge.label}
                        </span>
                      </div>
                      
                      {/* Basic Info */}
                      <div className="absolute bottom-3 left-3 right-3">
                        <h3 className="font-bold text-lg text-white">{getNameForDisplay(member)}</h3>
                        <p className="text-white/80 text-sm flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {member.location} · {member.age}세 · {member.gender === "male" ? "남성" : "여성"}
                        </p>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4">
                      <p className="text-sm text-stone-600 line-clamp-2 mb-3">
                        {member.introduction}
                      </p>
                      
                      {/* Interests */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        {member.interests.slice(0, 3).map((interest, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-stone-100 text-stone-600 text-xs rounded-full"
                          >
                            {interest}
                          </span>
                        ))}
                      </div>
                      
                      <button className="w-full py-2 text-sm text-burgundy-600 font-medium hover:bg-burgundy-50 rounded-lg transition-colors flex items-center justify-center gap-1">
                        프로필 보기
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-10 flex flex-col items-center gap-3" data-testid="members-pagination">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setCurrentPage(1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  disabled={safeCurrentPage === 1}
                  className="px-3 py-2 text-sm rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="첫 페이지"
                >«</button>
                <button
                  onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  disabled={safeCurrentPage === 1}
                  className="px-3 py-2 text-sm rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="이전 페이지"
                >‹ 이전</button>

                {(() => {
                  // 페이지 번호 7개까지 동적 표시 (현재 페이지 중심)
                  const pages = [];
                  const range = 2;
                  let start = Math.max(1, safeCurrentPage - range);
                  let end = Math.min(totalPages, safeCurrentPage + range);
                  if (safeCurrentPage <= range + 1) end = Math.min(totalPages, range * 2 + 1);
                  if (safeCurrentPage >= totalPages - range) start = Math.max(1, totalPages - range * 2);
                  if (start > 1) {
                    pages.push(<button key={1} onClick={() => { setCurrentPage(1); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="w-9 h-9 text-sm rounded-lg border border-stone-200 hover:bg-stone-50">1</button>);
                    if (start > 2) pages.push(<span key="dots-1" className="px-2 text-stone-400">…</span>);
                  }
                  for (let p = start; p <= end; p++) {
                    const active = p === safeCurrentPage;
                    pages.push(
                      <button
                        key={p}
                        onClick={() => { setCurrentPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className={`w-9 h-9 text-sm rounded-lg border transition-all ${
                          active ? "bg-burgundy-600 text-white border-burgundy-600 font-bold shadow" : "border-stone-200 hover:bg-stone-50 text-stone-700"
                        }`}
                        aria-current={active ? "page" : undefined}
                      >{p}</button>
                    );
                  }
                  if (end < totalPages) {
                    if (end < totalPages - 1) pages.push(<span key="dots-2" className="px-2 text-stone-400">…</span>);
                    pages.push(<button key={totalPages} onClick={() => { setCurrentPage(totalPages); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="w-9 h-9 text-sm rounded-lg border border-stone-200 hover:bg-stone-50">{totalPages}</button>);
                  }
                  return pages;
                })()}

                <button
                  onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  disabled={safeCurrentPage === totalPages}
                  className="px-3 py-2 text-sm rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="다음 페이지"
                >다음 ›</button>
                <button
                  onClick={() => { setCurrentPage(totalPages); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  disabled={safeCurrentPage === totalPages}
                  className="px-3 py-2 text-sm rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="마지막 페이지"
                >»</button>
              </div>
              <p className="text-xs text-stone-500">
                {((safeCurrentPage - 1) * PAGE_SIZE + 1).toLocaleString()}–{Math.min(safeCurrentPage * PAGE_SIZE, totalCount).toLocaleString()} / {totalCount.toLocaleString()}명
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Profile Detail Modal */}
      <AnimatePresence>
        {showProfileModal && selectedMember && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowProfileModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[95vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Profile Header */}
              {(() => {
                const canView = canIViewPhoto(selectedMember.membershipType);
                const badge = getMembershipBadge(selectedMember.membershipType);
                return (
                  <div className="relative h-44">
                    {canView ? (
                      selectedMember.profileImage ? (
                        <img
                          src={selectedMember.profileImage}
                          alt={selectedMember.username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <AnimatedAvatar name={selectedMember.username} gender={selectedMember.gender} />
                      )
                    ) : (
                      <>
                        {selectedMember.profileImage ? (
                          <img
                            src={selectedMember.profileImage}
                            alt={selectedMember.username}
                            className="w-full h-full object-cover blur-xl scale-110"
                          />
                        ) : (
                          <div className="w-full h-full blur-sm">
                            <AnimatedAvatar name={selectedMember.username} gender={selectedMember.gender} />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-stone-900/50 flex flex-col items-center justify-center">
                          <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3">
                            <Lock className="w-10 h-10 text-white" />
                          </div>
                          <p className="text-white font-medium text-center px-4">
                            {!isAuthenticated ? "로그인하시면 프로필을 볼 수 있어요" : "멤버십 업그레이드가 필요해요"}
                          </p>
                          <p className="text-white/70 text-sm mt-2">
                            {badge.label} 이상 회원만 열람 가능
                          </p>
                          {!isAuthenticated ? (
                            <Button
                              size="sm"
                              className="mt-4 bg-burgundy-500 hover:bg-burgundy-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowProfileModal(false);
                                navigate("/");
                              }}
                            >
                              로그인하기
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="mt-4 bg-gold-500 hover:bg-gold-600 text-stone-900"
                              onClick={(e) => {
                                e.stopPropagation();
                                alert("멤버십 업그레이드 페이지로 이동합니다. (추후 구현 예정)");
                              }}
                            >
                              멤버십 업그레이드
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
                    
                    <button
                      onClick={() => setShowProfileModal(false)}
                      className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-colors z-10"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                    
                    {/* Online & Badge */}
                    <div className="absolute top-4 left-4 flex gap-2 z-10">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        selectedMember.isOnline 
                          ? "bg-green-500 text-white" 
                          : "bg-stone-500/80 text-white"
                      }`}>
                        {selectedMember.isOnline ? "온라인" : selectedMember.lastActive}
                      </span>
                      {(() => {
                        const BadgeIcon = badge.icon;
                        return (
                          <span className={`px-3 py-1 text-xs font-bold rounded-full flex items-center gap-1 ${badge.color}`}>
                            <BadgeIcon className="w-3 h-3" />
                            {badge.label}
                          </span>
                        );
                      })()}
                    </div>
                    
                    {/* Name & Location */}
                    <div className="absolute bottom-3 left-4 right-4 z-10">
                      <h2 className="text-xl font-[720] text-white">{getNameForDisplay(selectedMember)}</h2>
                      <p className="text-white/80 text-sm flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" />
                        {selectedMember.location} · {selectedMember.age}세 · {selectedMember.gender === "male" ? "남성" : "여성"}
                      </p>
                    </div>
                  </div>
                );
              })()}
              
              {/* Profile Content */}
              <div className="px-5 py-4">
                {(() => {
                  const targetLevel = MEMBERSHIP_LEVELS[selectedMember.membershipType] ?? 0;
                  const viewerLevel = MEMBERSHIP_LEVELS[myMembership] ?? 0;
                  const isTargetRegularOrAbove = targetLevel >= 1;
                  const isViewerRegularOrAbove = isAuthenticated && viewerLevel >= 1;

                  return (
                    <>
                      {/* 기본 프로필 정보 */}
                      <div className="space-y-3 mb-4">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-stone-50 rounded-lg p-2.5">
                            <p className="text-xs text-stone-400 mb-0.5">나이</p>
                            <p className="text-sm font-medium text-stone-700">{selectedMember.age}세</p>
                          </div>
                          <div className="bg-stone-50 rounded-lg p-2.5">
                            <p className="text-xs text-stone-400 mb-0.5">성별</p>
                            <p className="text-sm font-medium text-stone-700">{selectedMember.gender === "male" ? "남성" : "여성"}</p>
                          </div>
                          <div className="bg-stone-50 rounded-lg p-2.5">
                            <p className="text-xs text-stone-400 mb-0.5">거주지</p>
                            <p className="text-sm font-medium text-stone-700">{selectedMember.location}</p>
                          </div>
                          {selectedMember.job && (
                            <div className="bg-stone-50 rounded-lg p-2.5">
                              <p className="text-xs text-stone-400 mb-0.5">직업</p>
                              <p className="text-sm font-medium text-stone-700">{selectedMember.job}</p>
                            </div>
                          )}
                        </div>

                        {selectedMember.interests && selectedMember.interests.length > 0 && (
                          <div>
                            <p className="text-xs text-stone-400 mb-1">취미/관심사</p>
                            <div className="flex flex-wrap gap-1">
                              {selectedMember.interests.map((interest, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-stone-100 text-stone-600 text-sm rounded-full">{interest}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {selectedMember.introduction && (
                          <div>
                            <p className="text-xs text-stone-400 mb-0.5">자기소개</p>
                            <p className="text-sm text-stone-600 leading-relaxed">{selectedMember.introduction}</p>
                          </div>
                        )}
                      </div>

                      {/* 정회원 이상 대상: 상세 프로필 (성격, 매칭점수) */}
                      {isTargetRegularOrAbove && (
                        <>
                          {selectedMember.personality && selectedMember.personality.length > 0 && (
                            <div className="mb-3">
                              <h3 className="font-bold text-stone-800 text-sm mb-1.5">성격</h3>
                              <div className="flex flex-wrap gap-1.5">
                                {selectedMember.personality.map((p, idx) => (
                                  <span key={idx} className="px-2.5 py-1 bg-stone-100 text-stone-600 text-xs rounded-full">{p}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {selectedMember.matchScore != null && (
                            <div className="mb-3 p-3 bg-stone-50 rounded-xl border border-stone-200">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-stone-200 rounded-xl flex items-center justify-center">
                                  <span className="text-sm font-bold text-stone-700">{Math.round(selectedMember.matchScore)}</span>
                                </div>
                                <div>
                                  <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
                                    AI 매칭 점수
                                    <Sparkles className="w-4 h-4 text-stone-500" />
                                  </h3>
                                  <p className="text-xs text-stone-500">100점 만점</p>
                                </div>
                              </div>
                              {selectedMember.matchReasons?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {selectedMember.matchReasons.map((reason, idx) => (
                                    <span key={idx} className="px-2 py-1 bg-white text-stone-600 text-xs rounded-full border border-stone-200 flex items-center gap-1">
                                      <TrendingUp className="w-3 h-3 text-stone-500" />
                                      {reason}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* 상세 프로필 (정회원 열람) */}
                      {isViewerRegularOrAbove && memberFullProfile && (
                        <div className="space-y-3 mb-4">
                          {(memberFullProfile.height || memberFullProfile.weight || memberFullProfile.mbti || memberFullProfile.blood_type) && (
                            <div className="bg-stone-50 rounded-lg p-3">
                              <p className="text-xs font-semibold text-stone-400 mb-2">외모/신체</p>
                              <div className="grid grid-cols-3 gap-2">
                                {memberFullProfile.height && <div><p className="text-[10px] text-stone-400">키</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.height}</p></div>}
                                {memberFullProfile.weight && <div><p className="text-[10px] text-stone-400">몸무게</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.weight}</p></div>}
                                {memberFullProfile.mbti && <div><p className="text-[10px] text-stone-400">MBTI</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.mbti}</p></div>}
                                {memberFullProfile.blood_type && <div><p className="text-[10px] text-stone-400">혈액형</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.blood_type}</p></div>}
                                {memberFullProfile.appearance_style && <div><p className="text-[10px] text-stone-400">스타일</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.appearance_style}</p></div>}
                                {memberFullProfile.charm_point && <div><p className="text-[10px] text-stone-400">매력포인트</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.charm_point}</p></div>}
                              </div>
                            </div>
                          )}
                          {(memberFullProfile.marital_status || memberFullProfile.has_children) && (
                            <div className="bg-stone-50 rounded-lg p-3">
                              <p className="text-xs font-semibold text-stone-400 mb-2">결혼/자녀</p>
                              <div className="grid grid-cols-2 gap-2">
                                {memberFullProfile.marital_status && <div><p className="text-[10px] text-stone-400">싱글여부</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.marital_status}</p></div>}
                                {memberFullProfile.has_children && <div><p className="text-[10px] text-stone-400">자녀</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.has_children === "yes" ? "있음" : "없음"}</p></div>}
                              </div>
                            </div>
                          )}
                          {(memberFullProfile.education || memberFullProfile.school_name || memberFullProfile.company_name) && (
                            <div className="bg-stone-50 rounded-lg p-3">
                              <p className="text-xs font-semibold text-stone-400 mb-2">학력/직업</p>
                              <div className="grid grid-cols-2 gap-2">
                                {memberFullProfile.education && <div><p className="text-[10px] text-stone-400">학력</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.education}</p></div>}
                                {memberFullProfile.school_name && <div><p className="text-[10px] text-stone-400">학교</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.school_name}</p></div>}
                                {memberFullProfile.company_name && <div><p className="text-[10px] text-stone-400">회사</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.company_name}</p></div>}
                                {memberFullProfile.position && <div><p className="text-[10px] text-stone-400">직위</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.position}</p></div>}
                              </div>
                            </div>
                          )}
                          {(memberFullProfile.religion || memberFullProfile.drinking || memberFullProfile.smoking) && (
                            <div className="bg-stone-50 rounded-lg p-3">
                              <p className="text-xs font-semibold text-stone-400 mb-2">성격/생활</p>
                              <div className="grid grid-cols-3 gap-2">
                                {memberFullProfile.religion && <div><p className="text-[10px] text-stone-400">종교</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.religion}</p></div>}
                                {memberFullProfile.drinking && <div><p className="text-[10px] text-stone-400">음주</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.drinking}</p></div>}
                                {memberFullProfile.smoking && <div><p className="text-[10px] text-stone-400">흡연</p><p className="text-xs font-medium text-stone-700">{memberFullProfile.smoking}</p></div>}
                              </div>
                            </div>
                          )}
                          {(memberFullProfile.life_motto || memberFullProfile.favorite_movie || memberFullProfile.important_values) && (
                            <div className="bg-stone-50 rounded-lg p-3">
                              <p className="text-xs font-semibold text-stone-400 mb-2">가치관</p>
                              <div className="space-y-1">
                                {memberFullProfile.life_motto && <div><p className="text-[10px] text-stone-400">좌우명</p><p className="text-xs text-stone-700">{memberFullProfile.life_motto}</p></div>}
                                {memberFullProfile.favorite_movie && <div><p className="text-[10px] text-stone-400">인생영화</p><p className="text-xs text-stone-700">{memberFullProfile.favorite_movie}</p></div>}
                                {memberFullProfile.important_values && <div><p className="text-[10px] text-stone-400">중요한 가치</p><p className="text-xs text-stone-700">{memberFullProfile.important_values}</p></div>}
                                {memberFullProfile.future_vision && <div><p className="text-[10px] text-stone-400">10년 뒤 모습</p><p className="text-xs text-stone-700">{memberFullProfile.future_vision}</p></div>}
                              </div>
                            </div>
                          )}
                          {(memberFullProfile.ideal_type || memberFullProfile.marriage_goal) && (
                            <div className="bg-burgundy-50 rounded-lg p-3 border border-burgundy-100">
                              <p className="text-xs font-semibold text-burgundy-400 mb-2">이상형/결혼관</p>
                              {memberFullProfile.ideal_type && <div className="mb-1"><p className="text-[10px] text-burgundy-300">이상형</p><p className="text-xs text-stone-700">{memberFullProfile.ideal_type}</p></div>}
                              {memberFullProfile.marriage_goal && <div><p className="text-[10px] text-burgundy-300">결혼 목표</p><p className="text-xs text-stone-700">{memberFullProfile.marriage_goal}</p></div>}
                            </div>
                          )}
                        </div>
                      )}

                      {/* 관심 보내기 & 메시지 보내기 */}
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          className={`flex-1 ${interestSent ? "border-green-400 text-green-600" : ""}`}
                          onClick={handleSendInterest}
                          disabled={sendingInterest || interestSent}
                          data-testid="send-interest-btn"
                        >
                          {interestSent ? <Check className="w-4 h-4 mr-2" /> : <Heart className="w-4 h-4 mr-2" />}
                          {interestSent ? "관심 전송됨" : sendingInterest ? "보내는 중..." : "관심 보내기"}
                        </Button>
                        <Button
                          className={`flex-1 text-stone-700 border border-stone-200 bg-white ${messageSent ? "bg-green-600 hover:bg-green-700 text-white border-green-600" : ""} ${!isAuthenticated ? "cursor-not-allowed" : "hover:bg-stone-50"}`}
                          onClick={() => {
                            if (!isAuthenticated) { setAccessGuard("auth"); setShowGuardModal(true); return; }
                            if (!canSendDM(user)) { setAccessGuard("dm_upgrade"); setShowGuardModal(true); return; }
                            setShowMessageModal(true);
                          }}
                          disabled={messageSent}
                          data-testid="send-message-btn"
                          style={!isAuthenticated ? { cursor: 'not-allowed' } : {}}
                        >
                          {messageSent ? <Check className="w-4 h-4 mr-2" /> : (!isAuthenticated || !canSendDM(user)) ? <Lock className="w-4 h-4 mr-2 text-red-500" /> : <Mail className="w-4 h-4 mr-2" />}
                          {messageSent ? "메시지 전송됨" : "메시지 보내기"}
                          {isAuthenticated && !canSendDM(user) && !messageSent && (
                            <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded ml-1.5">정회원</span>
                          )}
                        </Button>
                      </div>
                      {!isAuthenticated && (
                        <p className="text-sm font-medium text-center mt-3" style={{ color: '#722F37' }}>
                          로그인 후 메시지를 보낼 수 있습니다
                        </p>
                      )}
                      {isAuthenticated && !canSendDM(user) && (
                        <p className="text-xs text-stone-400 text-center mt-2">
                          쪽지 보내기는 정회원 전용 기능이에요
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 업그레이드 안내 모달 */}
      <AnimatePresence>
        {showUpgradeModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setShowUpgradeModal(false)}
            data-testid="upgrade-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Lock className="w-7 h-7 text-stone-400" />
              </div>
              <h3 className="text-lg font-bold text-stone-800 mb-2">정회원 이상 프로필</h3>
              <p className="text-sm text-stone-500 mb-6">
                정회원 이상의 프로필을 열람하려면<br />멤버십 업그레이드가 필요합니다.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowUpgradeModal(false)}>닫기</Button>
                <Button
                  className="flex-1 bg-stone-800 hover:bg-stone-900 text-white"
                  onClick={() => { setShowUpgradeModal(false); navigate("/membership"); }}
                  data-testid="upgrade-btn"
                >
                  멤버십 보기
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 메시지 보내기 모달 */}
      <AnimatePresence>
        {showMessageModal && selectedMember && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => { setShowMessageModal(false); setMessageContent(""); }}
            data-testid="message-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-stone-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-stone-500" />
                  <h3 className="font-bold text-stone-800">메시지 보내기</h3>
                </div>
                <button onClick={() => { setShowMessageModal(false); setMessageContent(""); }} className="text-stone-400 hover:text-stone-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4 p-3 bg-stone-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center text-sm font-semibold text-stone-600">
                    {getNameForDisplay(selectedMember)?.[0]}
                  </div>
                  <div>
                    <p className="font-medium text-stone-700">{getNameForDisplay(selectedMember)}</p>
                    <p className="text-xs text-stone-400">에게 메시지 보내기</p>
                  </div>
                </div>
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder="메시지 내용을 입력하세요..."
                  className="w-full h-32 p-3 border border-stone-200 rounded-xl text-sm text-stone-700 resize-none focus:outline-none focus:ring-2 focus:ring-stone-300 focus:border-transparent"
                  data-testid="message-textarea"
                  maxLength={500}
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-stone-400">{messageContent.length}/500</span>
                </div>
              </div>
              <div className="p-5 pt-0 flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => { setShowMessageModal(false); setMessageContent(""); }}>취소</Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-burgundy-500 to-burgundy-700 hover:from-burgundy-600 hover:to-burgundy-800 text-white shadow-md hover:shadow-lg transition-all"
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !messageContent.trim()}
                  data-testid="send-message-submit"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingMessage ? "전송 중..." : "보내기"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </>)}
    </div>
  );
}
